/**
 * The route tree.
 *
 * Code-based rather than file-based on purpose: the tree is the clearest single
 * statement of what this app is, and it maps one-to-one onto spec 002 §3's site
 * map, so the two can be read side by side.
 *
 * Each route's `loader` primes the query cache before the component renders,
 * which preserves the ordering the async server components had: data first,
 * then markup. Components then read the same query with `useSuspenseQuery` and
 * never see a loading state they have to handle themselves.
 */

import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

import { AdminLayout } from './layouts/admin-layout';
import { AuthLayout } from './layouts/auth-layout';
import { RootLayout } from './layouts/root-layout';
import { StoreLayout } from './layouts/store-layout';
import { NotFound } from './layouts/not-found';
import { RouteError, RoutePending } from './layouts/route-error';

import { STOREFRONT_OPEN } from './storefront';

import AccountPage from './pages/account';
import AdminDashboard from './pages/admin-dashboard';
import AdminDesignsPage from './pages/admin-designs';
import AdminMethodsPage from './pages/admin-methods';
import AdminJobsPage from './pages/admin-jobs';
import AdminMachinesPage from './pages/admin-machines';
import AdminStockPage from './pages/admin-stock';
import AdminStockValuePage from './pages/admin-stock-value';
import AdminStockTakePage from './pages/admin-stock-take';
import AdminStockItemPage from './pages/admin-stock-item';
import AdminLocationsPage from './pages/admin-locations';
import AdminWastePage from './pages/admin-waste';
import AdminReorderPage from './pages/admin-reorder';
import AdminRecipesPage from './pages/admin-recipes';
import AdminSuppliersPage from './pages/admin-suppliers';
import AdminSupplierPage from './pages/admin-supplier';
import AdminPurchaseOrdersPage from './pages/admin-purchase-orders';
import AdminPermissionsPage from './pages/admin-permissions';
import AdminUsersPage from './pages/admin-users';
import AdminDocumentsPage from './pages/admin-documents';
import AdminOrdersPage from './pages/admin-orders';
import AdminAccountPage from './pages/admin-account';
import AdminOrderListPage from './pages/admin-orders-list';
import AdminXeroPage from './pages/admin-xero';
import AdminSettingsPage from './pages/admin-settings';
import AdminHelpPage from './pages/admin-help';
import AdminCustomersPage from './pages/admin-customers';
import AdminProspectsPage from './pages/admin-prospects';
import AdminMaterialsPage from './pages/admin-materials';
import AdminIntegrationsPage from './pages/admin-integrations';
import AdminProductListingPage from './pages/admin-product-listing';
import AdminStylePage from './pages/admin-style';
import AdminProductsPage from './pages/admin-products';
import AdminQuoteDetail from './pages/admin-quote-detail';
import AdminQuotesPage from './pages/admin-quotes';
import BrandPage from './pages/shop-brand';
import CartPage from './pages/cart';
import CheckoutPage from './pages/checkout';
import CustomPage from './pages/custom';
import LandingPage from './pages/home';
import OrderDetailPage from './pages/order-detail';
import OrdersPage from './pages/orders';
import ProductPage from './pages/product';
import ProofPage from './pages/quote-proof';
import QuoteDetailPage from './pages/quote-detail';
import QuoteRequestPage from './pages/custom-quote';
import ShopPage from './pages/shop';
import ForgotPasswordPage from './pages/forgot-password';
import ResetPasswordPage from './pages/reset-password';
import SignInPage from './pages/sign-in';
import StudioPage from './pages/studio';

import {
  accountQuery,
  adminDashboardQuery,
  adminDesignsQuery,
  adminJobsQuery,
  adminMethodsQuery,
  adminOrdersQuery,
  adminIntegrationsQuery,
  adminProductsQuery,
  adminQuoteQuery,
  adminQuotesQuery,
  adminShellQuery,
  customQuery,
  footerQuery,
  tenantQuery,
  homeQuery,
  meQuery,
  orderQuery,
  ordersQuery,
  productQuery,
  quoteQuery,
  shopQuery,
  studioQuery,
  stockItemQuery,
  adminHelpQuery,
  adminMaterialsQuery,
  adminSettingsQuery,
  adminStockTakesQuery,
  itemMovementsQuery,
  adminLocationsQuery,
  adminWasteQuery,
  adminLowStockQuery,
  adminRecipesQuery,
  adminSuppliersQuery,
  adminPurchaseOrdersQuery,
  adminPermissionsQuery,
  adminUsersQuery,
  documentsQuery,
  styleQuery,
  supplierQuery,
} from './queries';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  // The wordmark is in every layout, storefront and admin alike, so the tenant
  // is loaded once at the root rather than by each branch. R-140.
  loader: ({ context }) => context.queryClient.ensureQueryData(tenantQuery),
  component: RootLayout,
  notFoundComponent: NotFound,
  // On the root as well as on the router. A child route with no error
  // component of its own bubbles its failure up to here, and without this the
  // whole tree unmounted: no shell, no message, an empty page. R-143.
  errorComponent: RouteError,
});

/* ------------------------------- storefront ------------------------------- */

/**
 * The whole storefront hangs off this route, which is what makes closing it one
 * decision rather than fourteen.
 *
 * `beforeLoad` runs on the parent before any child loader, so a closed store
 * redirects BEFORE `/shop` asks the database for a catalogue nobody is going to
 * see. Gating each child instead would run every loader first and then throw the
 * answer away.
 *
 * The sign-in route is deliberately NOT a child of this one any more. It used to
 * be, and leaving it here would mean the gate redirects the sign-in page to
 * itself, for ever.
 */
const storeRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'store',
  /**
   * The shop is shut to the PUBLIC, not to the studio. R-254.
   *
   * This used to bounce everybody, and the bounce lands on the sign-in route,
   * which sends an internal session straight to /admin. So every storefront
   * link inside the back office silently became a link to the dashboard.
   *
   * The one that mattered: the Orders register lists 12,655 orders and its
   * reference links to /orders/OG-16657, the only page that shows what is in
   * an order. Clicking it put you on the dashboard. The register looked like it
   * worked and answered nothing.
   *
   * Staff reading an order are not the public, so they go through. A customer,
   * or nobody at all, still meets the closed door.
   */
  beforeLoad: async ({ context }) => {
    if (STOREFRONT_OPEN) return;
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.isInternal) throw redirect({ to: '/auth/sign-in' });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(footerQuery),
  component: StoreLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/',
  /**
   * THE FRONT DOOR SENDS EACH PERSON WHERE THEY WERE GOING.
   *
   * Guy typed `app.obscust.com.au`, got the shop's landing page, and asked why
   * this screen was appearing. It is a fair question: he has never sold
   * anything through that page, the storefront is not open, and the tool he
   * signed in to use is at `/admin`.
   *
   * He reached it because R-254 deliberately lets STAFF through the storefront
   * gate. That rule is right and stays: a staff member following an order link
   * to `/orders/OG-16657` must not be bounced to the dashboard. It is right
   * about the inside of the shop and wrong about its front door.
   *
   * So the door, and only the door, sorts people:
   *
   *   signed in, staff      /admin, the thing they came for
   *   anybody else          the parent gate, which sends them to sign in while
   *                         the storefront is shut
   *
   * `/shop` is untouched, so a staff member who wants to see what a customer
   * sees still can. And when the storefront opens, a customer arriving here
   * gets the landing page exactly as before: this only ever moves staff.
   */
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me.isInternal) throw redirect({ to: '/admin' });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(homeQuery),
  component: LandingPage,
});

const shopRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/shop',
  loader: ({ context }) => context.queryClient.ensureQueryData(shopQuery()),
  component: ShopPage,
});

const brandRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/shop/$brand',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(shopQuery(params.brand)),
  component: BrandPage,
});

const productRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/product/$id',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(productQuery(params.id)),
  component: ProductPage,
});

const customRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/custom',
  loader: ({ context }) => context.queryClient.ensureQueryData(customQuery),
  component: CustomPage,
});

const studioRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/custom/studio/$productId',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(studioQuery(params.productId)),
  component: StudioPage,
});

const quoteRequestRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/custom/quote',
  loader: ({ context }) => context.queryClient.ensureQueryData(customQuery),
  component: QuoteRequestPage,
});

const cartRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/cart',
  component: CartPage,
});

const checkoutRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/checkout',
  loader: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: CheckoutPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/orders',
  loader: ({ context }) => context.queryClient.ensureQueryData(ordersQuery),
  component: OrdersPage,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/orders/$ref',
  loader: ({ context, params, location }) =>
    context.queryClient.ensureQueryData(
      orderQuery(params.ref, (location.search as { t?: string }).t),
    ),
  component: OrderDetailPage,
});

const quoteDetailRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/quotes/$ref',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(quoteQuery(params.ref)),
  component: QuoteDetailPage,
});

const quoteProofRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/quotes/$ref/proof',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(quoteQuery(params.ref)),
  component: ProofPage,
});

const accountRoute = createRoute({
  getParentRoute: () => storeRoute,
  path: '/account',
  loader: ({ context }) => context.queryClient.ensureQueryData(accountQuery),
  component: AccountPage,
});

/* --------------------------------- auth ----------------------------------- */

/**
 * Sign-in sits outside the storefront branch so that it survives the storefront
 * being closed, and wears `AuthLayout`: the wordmark, and nothing else.
 */
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
});

const signInRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/auth/sign-in',
  beforeLoad: async ({ context }) => {
    // Somebody who is already signed in has no business looking at a sign-in
    // form. Without this, the staff member whose session is still good gets the
    // form again after the store closes, types a correct password, and lands
    // exactly where they already were.
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me.isInternal) throw redirect({ to: '/admin' });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: SignInPage,
});

/**
 * Both reset screens are reachable while signed OUT, which is the whole point,
 * so neither carries the `isInternal` bounce the sign-in route has.
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/auth/forgot',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/auth/reset',
  // The token arrives in the query string and is read by the component. It is
  // deliberately NOT validated into typed search params: a malformed one must
  // reach the page so it can say "that link is incomplete", rather than being
  // turned into a router error with no explanation.
  component: ResetPasswordPage,
});

/* --------------------------------- admin ---------------------------------- */

/**
 * NFR-03. This redirect is a courtesy, not the gate: it stops a signed-out
 * person staring at a broken screen. The gate that matters refuses the data in
 * server/api.ts, and RLS refuses it again underneath. A client-side check is
 * never access control.
 */
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin',
  beforeLoad: async ({ context, location }) => {
    // Not prefetched: this one IS the gate, and it needs the answer.
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.isInternal) {
      // The address that was asked for, not the constant '/admin'. This gate is
      // on the PARENT route, so it catches every admin page, and sending a
      // constant made every one of them forget where it was going. See
      // src/lib/next-path.ts.
      throw redirect({ to: '/auth/sign-in', search: { next: location.href } });
    }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(adminShellQuery),
  component: AdminLayout,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminDashboardQuery('30d')),
  component: AdminDashboard,
});

const adminQuotesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/quotes',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminQuotesQuery),
  component: AdminQuotesPage,
});

const adminQuoteDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/quotes/$ref',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(adminQuoteQuery(params.ref)),
  component: AdminQuoteDetail,
});

const adminJobsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/jobs',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminJobsQuery),
  component: AdminJobsPage,
});

const adminMachinesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/machines',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminJobsQuery),
  component: AdminMachinesPage,
});

const adminStockRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/stock',
  // Which item is open OVER the list. A link somebody can send.
  validateSearch: (search: Record<string, unknown>): { open?: string } => ({
    open: typeof search.open === 'string' ? search.open : undefined,
  }),
  // No loader. The stock list is a page whose contents depend on the search
  // box and the page number, both of which live in the component, so a loader
  // would prefetch page one of a list nobody asked for and then be replaced.
  component: AdminStockPage,
});

const adminStockItemRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/stock/$itemId',
  // Same as the admin order page: the item opens in a panel over the stock
  // list now, and this path stays so that links already in a history work.
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/admin/stock', search: { open: params.itemId } });
  },
  loader: ({ context, params }) =>
    // The locations are needed by the adjustment form, which is on this page.
    Promise.all([
      context.queryClient.ensureQueryData(stockItemQuery(params.itemId)),
      context.queryClient.ensureQueryData(adminLocationsQuery),
      context.queryClient.ensureQueryData(itemMovementsQuery(params.itemId)),
    ]),
  component: AdminStockItemPage,
});

const adminLocationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/locations',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminLocationsQuery),
  component: AdminLocationsPage,
});

const adminDocumentsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/how-we-do-things',
  loader: ({ context }) => context.queryClient.ensureQueryData(documentsQuery),
  component: AdminDocumentsPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/people',
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminUsersQuery),
      // The role picker is built from the roles the matrix knows about, so a
      // role added there appears here without a second list to maintain.
      context.queryClient.ensureQueryData(adminPermissionsQuery),
    ]),
  component: AdminUsersPage,
});

const adminPermissionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/permissions',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminPermissionsQuery),
  component: AdminPermissionsPage,
});

const adminPurchaseOrdersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/purchase-orders',
  // Spec 012 FR-1243 and FR-1244: the Dashboard links to the Late list.
  validateSearch: (search: Record<string, unknown>): { list?: 'open' | 'late' | 'all'; open?: string } => ({
    list: search.list === 'late' || search.list === 'all' || search.list === 'open' ? search.list : undefined,
    open: typeof search.open === 'string' ? search.open : undefined,
  }),
  loaderDeps: ({ search }) => ({ list: search.list ?? 'open' }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminPurchaseOrdersQuery(deps.list, 1)),
      // The receipt form needs somewhere to book stock in to.
      context.queryClient.ensureQueryData(adminLocationsQuery),
    ]),
  component: AdminPurchaseOrdersPage,
});

const adminSuppliersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/suppliers',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminSuppliersQuery),
  component: AdminSuppliersPage,
});

/** One supplier: details, contacts and the items it sells. Spec 013. */
const adminSupplierRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/suppliers/$supplierId',
  loader: ({ context, params }) => context.queryClient.ensureQueryData(supplierQuery(params.supplierId)),
  component: AdminSupplierPage,
});

const adminRecipesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/recipes',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminRecipesQuery),
  component: AdminRecipesPage,
});

const adminReorderRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/reorder',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminLowStockQuery),
  component: AdminReorderPage,
});

// Reports, Stock value. Spec 017. No loader: the day is chosen on the screen.
const adminStockValueRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/reports/stock-value',
  component: AdminStockValuePage,
});

const adminWasteRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/waste',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminWasteQuery('reason')),
  component: AdminWastePage,
});

const adminStockTakeRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/stock-takes',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminStockTakesQuery(1)),
  component: AdminStockTakePage,
});

/**
 * The board keeps the work, the register keeps the book.
 *
 * The board was at `/admin/orders` and held both jobs, badly: it is five
 * columns of cards, and 12,656 imported Shopify orders are not five columns of
 * anything. So the board moved to `/admin/pipeline`, which is what the menu
 * already called it, and `/admin/orders` is now the list of every order,
 * fulfilled and open. Each URL says what is on it.
 */
const adminPipelineRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/pipeline',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminOrdersQuery),
  component: AdminOrdersPage,
});

/**
 * Customers. No loader, because the register is searched and paged and the
 * first paint should not wait on fifty rows the reader may filter away.
 */
const adminCustomersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/customers',
  component: AdminCustomersPage,
});

/**
 * Prospects. Spec 009. The open record, the add panel and the follow-ups
 * filter live in the address, so a link to one can be sent and a phone can
 * keep Fast add on its home screen (FR-902).
 */
const adminProspectsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/prospects',
  validateSearch: (search: Record<string, unknown>): { open?: string; add?: string; due?: string } => ({
    open: typeof search.open === 'string' ? search.open : undefined,
    add: search.add === 'fast' || search.add === 'full' ? search.add : undefined,
    due: search.due === '1' ? '1' : undefined,
  }),
  component: AdminProspectsPage,
});

const adminMaterialsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/materials',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminMaterialsQuery),
  component: AdminMaterialsPage,
});

const adminHelpRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/help',
  /**
   * `?page=<slug>`, so a help page has an address.
   *
   * Without it the cross links at the bottom of every article pointed at
   * /admin/help and reopened the contents, which is a dead end dressed as a
   * next step. It also means somebody can send "read this one" as a link,
   * which is most of what onboarding is.
   */
  validateSearch: (search: Record<string, unknown>) => ({
    page: typeof search.page === 'string' ? search.page : undefined,
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(adminHelpQuery),
  component: AdminHelpPage,
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/settings',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminSettingsQuery),
  component: AdminSettingsPage,
});

const adminXeroRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/xero',
  // No loader: the log and the queue are paged in the component.
  component: AdminXeroPage,
});

/**
 * One order, inside the admin shell. R-258.
 *
 * The register linked to `/orders/OG-16687`, which lives under the STOREFRONT
 * branch, so opening an order from the back office dropped the reader onto the
 * shop: the shop's header, the shop's footer, a "Your orders" link to a list of
 * what they had personally bought, and no way back to the register. The order
 * was right and everything around it was somebody else's page.
 *
 * Same component, because the facts about an order do not change with who is
 * reading them. Different parent, because the furniture does.
 */
const adminOrderDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/orders/$ref',
  /**
   * KEPT, AND IT REDIRECTS.
   *
   * The register shows an order in a panel over the list now, so the order is
   * `?open=OG-29606` on the register's own route. This path is in browser
   * histories, in the dashboard's links and in emails people have sent.
   * Deleting it would turn every one of them into a 404 to save one route.
   *
   * The CUSTOMER'S order page at `/orders/$ref` is untouched. It is a page
   * somebody is sent a link to, with no list behind it to open over.
   */
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/admin/orders', search: { open: params.ref } });
  },
  component: OrderDetailPage,
});

/**
 * Your own account, for somebody who works here.
 *
 * `/account` is the CUSTOMER'S: a shipping address, marketing preferences and
 * a request to delete your account. Sending staff there was wrong in a way
 * that is not cosmetic, and Christian said so: "this looks like the account
 * for all users ... can we have a different account route for the admin".
 */
const adminAccountRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/account',
  component: AdminAccountPage,
});

const adminOrdersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/orders',
  // No loader: the page depends on the filter, the search box and the page
  // number, all of which live in the component.
  /**
   * `open` is the order shown in the panel. `ref` is the dashboard's "show me
   * this one" seed for the search box, and the two are different questions:
   * one opens an order, the other filters the list down to it.
   */
  /**
   * THE REGISTER PUTS ITS STATE IN THE URL, and that is what makes the back
   * button on an order work.
   *
   * The filter, the page number and the search box used to be React state, so
   * opening an order unmounted them and coming back gave you the top of page
   * one of everything. Working through twenty orders meant losing your place
   * twenty times.
   *
   * In the URL they are restored by the browser going back, along with the
   * scroll position, which the router already restores. Nothing has to
   * remember anything.
   *
   * `ref` is older and different: it is the dashboard's "show me this one"
   * seed for the search box.
   *
   * The return type is annotated with OPTIONAL keys. Inferred, they come out
   * as `string | undefined` and REQUIRED, and then every `<Link search={...}>`
   * in the app has to name all four to satisfy the type.
   */
  validateSearch: (search: Record<string, unknown>): {
    ref?: string; open?: string; filter?: string; page?: string; q?: string;
  } => ({
    ref: typeof search.ref === 'string' ? search.ref : undefined,
    // Which order is open OVER the list. A link somebody can send.
    open: typeof search.open === 'string' ? search.open : undefined,
    filter: typeof search.filter === 'string' ? search.filter : undefined,
    page: typeof search.page === 'string' ? search.page : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: AdminOrderListPage,
});

/**
 * The product record, separate from Publishing.
 *
 * Publishing composes a shop listing from a blank and a design. This is the
 * style itself: the words, the tags and the photographs. R-212.
 */
const adminProductListingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/product-listing',
  // No loader: the page it shows depends on the search box and the page
  // number, both of which live in the component.
  component: AdminProductListingPage,
});

/**
 * One style, whole: its words and photos, its colours and prices, and its stock
 * by size. Spec 010, tracker #10. Products, Publishing and Stock on hand open it.
 */
const adminStyleRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/styles/$productId',
  loader: ({ context, params }) => context.queryClient.ensureQueryData(styleQuery(params.productId)),
  component: AdminStylePage,
});

/**
 * The Shopify bridge: is it on, what is waiting, what needs a person. T112.
 *
 * It loads the catalogue as well as the bridge, because the one action this
 * screen offers is "which of our variants is this Shopify line", and a picker
 * with nothing in it is a screen that cannot fix anything.
 */
const adminIntegrationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/integrations',
  // The variant picker searches as you type now, so there is no catalogue to
  // preload. It used to pull every product with every variant embedded before
  // the screen would render at all.
  loader: ({ context }) => context.queryClient.ensureQueryData(adminIntegrationsQuery),
  component: AdminIntegrationsPage,
});

const adminProductsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/products',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminProductsQuery),
  component: AdminProductsPage,
});

const adminDesignsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/designs',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminDesignsQuery),
  component: AdminDesignsPage,
});

const adminMethodsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/methods',
  loader: ({ context }) => context.queryClient.ensureQueryData(adminMethodsQuery),
  component: AdminMethodsPage,
});



/* --------------------------------- tree ----------------------------------- */

export const routeTree = rootRoute.addChildren([
  storeRoute.addChildren([
    indexRoute,
    shopRoute,
    brandRoute,
    productRoute,
    customRoute,
    studioRoute,
    quoteRequestRoute,
    cartRoute,
    checkoutRoute,
    ordersRoute,
    orderDetailRoute,
    quoteDetailRoute,
    quoteProofRoute,
    accountRoute,
  ]),
  authRoute.addChildren([signInRoute, forgotPasswordRoute, resetPasswordRoute]),
  adminRoute.addChildren([
    adminIndexRoute,
    adminQuotesRoute,
    adminQuoteDetailRoute,
    adminJobsRoute,
    adminMachinesRoute,
    adminStockRoute,
    adminAccountRoute,
    adminStockItemRoute,
    adminLocationsRoute,
    adminStockTakeRoute,
    adminWasteRoute,
    adminStockValueRoute,
    adminReorderRoute,
    adminRecipesRoute,
    adminSuppliersRoute,
    adminSupplierRoute,
    adminPurchaseOrdersRoute,
    adminPermissionsRoute,
    adminUsersRoute,
    adminDocumentsRoute,
    adminOrderDetailRoute,
    adminCustomersRoute,
    adminProspectsRoute,
    adminMaterialsRoute,
    adminHelpRoute,
    adminSettingsRoute,
    adminXeroRoute,
    adminPipelineRoute,
    adminOrdersRoute,
    adminIntegrationsRoute,
    adminProductListingRoute,
    adminStyleRoute,
  adminProductsRoute,
    adminDesignsRoute,
    adminMethodsRoute,
  ]),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPendingMs: 150,
    // Without this a rejected loader shows the PREVIOUS screen with the new
    // URL, silently and for ever. R-143.
    defaultErrorComponent: RouteError,
    // Without this the router renders NOTHING while a loader runs, and a slow
    // answer is an empty page. R-184.
    defaultPendingComponent: RoutePending,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}

export { Outlet };
